import useCreateDocumentTemplate from '@/api/mutations/documentTemplate/useCreateDocumentTemplate';
import useUpdateDocumentTemplate from '@/api/mutations/documentTemplate/useUpdateDocumentTemplate';
import { DocumentTemplate } from '@/api/queries/me/useGetDocumentTemplate';
import UploadTemplateField from '@/ui/components/uploadTemplateField/UploadTemplateField';
import SidebarLayout from '@/ui/pages/shared/SidebarLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  ButtonRadioGroup,
  Card,
  Checkbox,
  Field,
  Form,
  RawInput,
  Textarea,
  useToastContext,
} from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import { downloadHtmlFile } from '@payaca/utilities/fileUtilities';
import { Nullish } from '@payaca/utilities/types';
import { expectedSignatoriesInputSchema } from '@payaca/validation/schemas';
import { FC, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import { z } from 'zod/v4';
import { AvailableDocumentTemplateVariablesList } from './AvailableDocumentTemplateVariablesList';

const FORM_SCHEMA = z
  .object({
    name: z.string().min(1, 'Name is required').max(1023),
    description: z.string().max(4095).optional().nullable(),
    templateHtml: z
      .object({
        preview: z.string(),
        raw: z.string(),
        usedVariables: z.array(z.string()),
      })
      .nullable()
      .refine((val) => val !== null, {
        message: 'Template HTML is required',
      }),
    expectedSignatories: expectedSignatoriesInputSchema.nullish(),
  })
  .refine(
    (data) => {
      const accessedSignatures = new Set(
        data.templateHtml?.usedVariables
          .filter((p) => p.startsWith('signatures.'))
          .map((p) => p.split('.')[1]) // second segment
      );

      const availableSignatures = new Set(
        data.expectedSignatories?.map((signatory) => signatory.identifier)
      );

      const unavailableSignatures =
        accessedSignatures.difference(availableSignatures);

      return unavailableSignatures.size === 0;
    },
    {
      path: ['expectedSignatories'],
      message:
        'The provided template uses signature variables, so this document must require a customer signature.',
    }
  )
  .refine(
    (data) => {
      const availableSignatures = data.expectedSignatories?.map(
        (signatory) => signatory.identifier
      );

      if (!availableSignatures?.length) return true;

      const allAvailableSignaturesAreUsed = availableSignatures?.every((sig) =>
        data.templateHtml?.usedVariables.includes(
          `signatures.${sig}.signatureImageUrl`
        )
      );

      return allAvailableSignaturesAreUsed;
    },
    {
      path: ['expectedSignatories'],
      message: `A customer signature is required, but the provided template doesn't include the signature`,
    }
  );

type TFormSchema = z.infer<typeof FORM_SCHEMA>;

const getDefaultValues = (template: Nullish<DocumentTemplate>) => {
  if (!template) return { templateHtml: null };

  return {
    name: template.name,
    description: template.description,
    templateHtml: {
      raw: template.templateHtml,
      preview: template.previewDummyDocumentInstance.documentHtml,
      usedVariables: template.usedVariables,
    },
    expectedSignatories: template.expectedSignatories || null,
  };
};

export const CreateEditDocumentTemplateForm: FC<{
  template: Nullish<DocumentTemplate>;
  onSuccess: () => void;
}> = (props) => {
  const { template, onSuccess } = props;

  const isEditing = !!template;

  const [isMobileView, setIsMobileView] = useState(false);

  const formMethods = useForm<TFormSchema>({
    defaultValues: getDefaultValues(template),
    resolver: zodResolver(FORM_SCHEMA),
  });

  const isDirty = formMethods.formState.isDirty;
  const templateHtml = formMethods.watch('templateHtml');
  const name = formMethods.watch('name');
  const expectedSignatories = formMethods.watch('expectedSignatories');

  const { mutateAsync: createDocumentTemplate, isPending: isCreating } =
    useCreateDocumentTemplate();

  const { mutateAsync: updateDocumentTemplate, isPending: isUpdating } =
    useUpdateDocumentTemplate();

  const isPersisting = isCreating || isUpdating;

  const suppressPromptRef = useRef(false);

  const { pushError } = useToastContext();

  const onSubmit: SubmitHandler<TFormSchema> = async (data) => {
    if (!data.templateHtml) {
      // We won't get here due to zod refinement, but TS doesn't know that
      return;
    }

    try {
      if (isEditing) {
        await updateDocumentTemplate({
          id: template.id,
          name: data.name,
          description: data.description,
          templateHtml: data.templateHtml?.raw,
          expectedSignatories: data.expectedSignatories,
        });
      } else {
        await createDocumentTemplate({
          name: data.name,
          description: data.description,
          templateHtml: data.templateHtml?.raw,
          expectedSignatories: data.expectedSignatories,
        });
      }
      suppressPromptRef.current = true;
      onSuccess();
    } catch (e) {
      pushError('Something went wrong');
    }
  };

  return (
    <Form methods={formMethods} onSubmit={onSubmit} className="h-full">
      <Prompt
        when={isDirty && !suppressPromptRef.current}
        message={() => {
          if (suppressPromptRef.current) return true;
          return 'You have unsaved changes. Are you sure you want to leave this page?';
        }}
      />
      <SidebarLayout>
        <SidebarLayout.Body>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h1>{isEditing ? 'Edit' : 'Create'} Document Template</h1>

              {!!templateHtml && (
                <ButtonRadioGroup
                  value={isMobileView}
                  sizeVariant="sm"
                  onChange={setIsMobileView}
                  options={[
                    {
                      label: 'Desktop',
                      value: false,
                    },
                    {
                      label: 'Mobile',
                      value: true,
                    },
                  ]}
                  CustomOption={({ option: { label, value } }) => {
                    return (
                      <>
                        <UntitledIcon
                          className="w-5 h-5"
                          name={value ? 'phone-01.3' : 'monitor-01.3'}
                        />
                        {label}
                      </>
                    );
                  }}
                />
              )}
            </div>

            <Controller<TFormSchema, 'templateHtml'>
              name="templateHtml"
              render={({ field: { value, onChange, name } }) => {
                return (
                  <UploadTemplateField
                    isMobile={isMobileView}
                    name={name}
                    value={value}
                    onChange={(template) => {
                      const expectedSignatories =
                        formMethods.getValues().expectedSignatories;

                      if (
                        template?.usedVariables.some((variable) =>
                          variable.startsWith('signatures.customer')
                        ) &&
                        !expectedSignatories?.some(
                          (signatory) => signatory.identifier === 'customer'
                        )
                      ) {
                        // The user has uploaded a template containing customer signature variables,
                        // but hasn't indicated that a customer signature is expected yet, so we
                        // automatically set the expected signatories to include the customer.
                        formMethods.setValue(
                          'expectedSignatories',
                          [{ role: 'customer', identifier: 'customer' }],
                          { shouldDirty: true }
                        );
                      }
                      onChange(template);
                    }}
                  />
                );
              }}
            />
            <div>
              <h4 className="mb-2">Available variables</h4>
              <Card>
                <Card.Body>
                  <AvailableDocumentTemplateVariablesList
                    input={{ expectedSignatories: expectedSignatories ?? null }}
                  />
                </Card.Body>
              </Card>
            </div>
          </div>
        </SidebarLayout.Body>

        <SidebarLayout.Sidebar>
          <SidebarLayout.Sidebar.Body>
            <div className="flex flex-col gap-4">
              <Field name={'name'}>
                <Field.Label>Template name</Field.Label>
                <Field.HelperText>
                  This will be the default name of any generated Documents
                </Field.HelperText>
                <RawInput {...formMethods.register('name')} />
              </Field>
              <Controller
                name="description"
                render={({ field }) => {
                  return (
                    <Field name={field.name}>
                      <Field.Label>Template description</Field.Label>
                      <Textarea
                        value={field.value}
                        onChange={field.onChange}
                        rows={3}
                      />
                    </Field>
                  );
                }}
              />
              {import.meta.env.VITE_ENABLE_DOCUMENT_SIGNATORIES === 'true' && (
                <Controller
                  name="expectedSignatories"
                  render={({ field }) => {
                    return (
                      <Field name={field.name}>
                        <Checkbox
                          label="Require customer signature"
                          checked={!!field.value?.length}
                          onChange={(checked) => {
                            if (!field.value?.length) {
                              field.onChange([
                                { role: 'customer', identifier: 'customer' },
                              ]);
                            } else {
                              field.onChange(null);
                            }
                          }}
                        />
                      </Field>
                    );
                  }}
                />
              )}
            </div>
          </SidebarLayout.Sidebar.Body>
          <SidebarLayout.Sidebar.Footer>
            <div className="space-y-4">
              {templateHtml && (
                <>
                  <Button
                    className="w-full"
                    variant="white"
                    onClick={() => downloadHtmlFile(templateHtml.raw, name)}
                  >
                    <UntitledIcon name="download-01.3" className="w-4 h-4" />
                    Download
                  </Button>
                  <Button
                    className="w-full"
                    variant="white"
                    onClick={() => {
                      formMethods.setValue('templateHtml', null, {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <UntitledIcon name="x-close" className="w-2.5 h-2.5" />
                    Clear
                  </Button>
                </>
              )}
              <Button
                type="submit"
                className="w-full"
                isProcessing={isPersisting}
                disabled={isPersisting}
              >
                {isEditing ? 'Update template' : 'Create template'}
              </Button>
            </div>
          </SidebarLayout.Sidebar.Footer>
        </SidebarLayout.Sidebar>
      </SidebarLayout>
    </Form>
  );
};
